import React, { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Location } from 'history'
import { DefaultTheme, ThemeProvider } from 'styled-components'

import {
  routes,
  RedirectBadge,
  IndexLayoutExtraProps,
  IndexLayoutContent,
  SignUpForm,
  IndexLayoutHeaderLink,
} from '@sketch/modules-common'
import { Link, ExternalLink } from '@sketch/components'

import { isMac } from '@sketch/utils'
import { useSignUpFormContextForAnalytics } from './useSignUpFormContextForAnalytics'

import {
  Line,
  OperatingSystemWarning,
  Container,
  TextSignUp,
  RequiredOSVersion,
} from './SignUpView.styles'

export interface BaseSignUpViewProps
  extends Pick<
    IndexLayoutExtraProps,
    'useOverrideLayoutProps' | 'HeaderPortal'
  > {
  email: string | undefined
  invitedUser: boolean
  onCompleted: () => void
}

const BaseSignUpView: React.FC<BaseSignUpViewProps> = ({
  email,
  invitedUser,
  onCompleted,
  useOverrideLayoutProps,
  HeaderPortal,
}) => {
  const { state } = useLocation<{ from: Location }>()
  const showOSWarning = !isMac()

  const getTheme = useCallback(
    (theme: DefaultTheme) => overriddenThemeFor(theme),
    []
  )

  useOverrideLayoutProps({ maxContainerWidth: '960px' })

  const formContextForAnalytics = useSignUpFormContextForAnalytics()

  return (
    <ThemeProvider theme={getTheme}>
      <IndexLayoutContent center="horizontal" marginTop paddingHorizontal>
        <HeaderPortal>
          <IndexLayoutHeaderLink />
        </HeaderPortal>
        {state?.from && <RedirectBadge {...state} />}
        <Container>
          <RequiredOSVersion>
            To run Sketch you’ll need a macOS device that meets our{' '}
            <ExternalLink href="http://sketch.com/redirect/web/system-requirements">
              system requirements
            </ExternalLink>
            .
          </RequiredOSVersion>
          <SignUpForm
            email={email}
            invitedUser={invitedUser}
            onCompleted={onCompleted}
            formContextForAnalytics={formContextForAnalytics}
          />
          {showOSWarning && (
            <OperatingSystemWarning data-testid="os-warning">
              To run Sketch you’ll need a macOS device that our{' '}
              <ExternalLink href="http://sketch.com/redirect/web/system-requirements">
                system requirements
              </ExternalLink>
              .
            </OperatingSystemWarning>
          )}
          <Line />
          <TextSignUp>
            Already have a Sketch account?{' '}
            <Link
              variant="primary"
              to={{
                pathname: routes.SIGN_IN.create({}),
                state,
              }}
              isUnderlined
            >
              Sign in
            </Link>
          </TextSignUp>
        </Container>
      </IndexLayoutContent>
    </ThemeProvider>
  )
}

const overriddenThemeFor = (theme: DefaultTheme) => theme

export default BaseSignUpView
